import React, { useEffect } from "react";
import { Download } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { MASTER, ORG_TYPES, CRITERIA, TEHSILS } from "@/mock/data";
import { TextField, SelectField, FileField, TextareaField, Fieldset, Criteria } from "@/components/fields/Field";
import { openPdf } from "@/lib/openPdf";

// Lightweight bound cell input for tables
function Cell({ name, value: presetValue, readOnly, invalid, disabled }) {
  const { application, setField } = useApp();
  const value = application.data[name] ?? presetValue ?? "";
  useEffect(() => {
    if (presetValue !== undefined && application.data[name] === undefined) setField(name, presetValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <input
      value={value}
      name={name}
      readOnly={readOnly}
      disabled={disabled}
      onChange={(e) => setField(name, e.target.value)}
      className={`w-full rounded-md border px-2.5 py-1.5 text-[13px] outline-none focus:border-[#1b5e3f] focus:ring-1 focus:ring-[#1b5e3f]/20 disabled:bg-slate-100 ${
        invalid ? "border-[#dc2626]" : "border-slate-300"
      }`}
    />
  );
}

function CellSelect({ name, options, invalid, disabled }) {
  const { application, setField } = useApp();
  const value = application.data[name] ?? options[0];
  return (
    <select
      value={value}
      name={name}
      disabled={disabled}
      onChange={(e) => setField(name, e.target.value)}
      className={`w-full rounded-md border px-2 py-1.5 text-[13px] outline-none focus:border-[#1b5e3f] disabled:bg-slate-100 ${
        invalid ? "border-[#dc2626]" : "border-slate-300"
      }`}
    >
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  );
}

// Single "होय" toggle circle for a table row
function PassRadio({ name }) {
  const { application, setField } = useApp();
  const checked = application.data[name] === "yes";
  return (
    <button
      type="button"
      onClick={() => setField(name, checked ? "" : "yes")}
      className="flex items-center gap-1.5"
    >
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors ${
          checked ? "border-[#1b5e3f] bg-[#1b5e3f]" : "border-slate-400 bg-white"
        }`}
      >
        {checked && <span className="h-2 w-2 rounded-full bg-white" />}
      </span>
      <span className={`text-[13px] font-semibold ${checked ? "text-[#1b5e3f]" : "text-slate-500"}`}>होय</span>
    </button>
  );
}

// ===================== STEP 1 : Personal =====================
const MR_TALUKA_TEHSIL = { "अमरावती": "Amravati", "भातकुली": "Bhatkuli", "नांदगाव खंडेश्वर": "Nandgaon Khandeshwar", "चांदूर रेल्वे": "Chandur Railway", "धामणगाव रेल्वे": "Dhamangaon Railway", "तिवसा": "Tiwsa", "मोर्शी": "Morshi", "वरुड": "Warud", "चांदूर बाजार": "Chandur Bazar", "अचलपूर": "Achalpur", "अंजनगाव सुर्जी": "Anjangaon Surji", "दर्यापूर": "Daryapur", "धारणी": "Dharni", "चिखलदरा": "Chikhaldara" };
const TEHSIL_TALUKA_MR = Object.fromEntries(Object.entries(MR_TALUKA_TEHSIL).map(([mr, en]) => [en, mr]));

export function Step1({ errors = {} }) {
  const { application, setField, user } = useApp();
  const taluka = application.data.taluka || "-- निवडा --";
  const dob = application.data.dob;
  const [circles, setCircles] = React.useState([]);
  const [localBodies, setLocalBodies] = React.useState([]);
  const [typeBodies, setTypeBodies] = React.useState([]);
  const [wards, setWards] = React.useState([]);
  const tehsilVal = application.data.tehsil;
  const orgTypeVal = application.data.orgType;
  const circleVal = application.data.circle;
  React.useEffect(() => {
    if (tehsilVal) {
      api.circlesFor(tehsilVal).then((cs) => {
        setCircles(cs || []);
        if (application.data.circle && !(cs || []).some((c) => c.id === application.data.circle)) {
          setField("circle", "");
        }
      }).catch(() => setCircles([]));
    } else {
      setCircles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tehsilVal]);

  const isGP = !orgTypeVal || orgTypeVal === "-- निवडा --" || orgTypeVal === "ग्रामपंचायत";

  // ग्रामपंचायत flow (unchanged): local bodies filtered by tehsil + मंडळ (circle)
  React.useEffect(() => {
    if (!isGP) return;
    if (orgTypeVal === "ग्रामपंचायत" && tehsilVal && circleVal) {
      api.localBodiesFor(tehsilVal, circleVal, orgTypeVal).then((lbs) => {
        setLocalBodies(lbs || []);
        if (application.data.village && !(lbs || []).includes(application.data.village)) {
          setField("village", "");
        }
      }).catch(() => setLocalBodies([]));
    } else {
      setLocalBodies([]);
      if (application.data.village) setField("village", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGP, orgTypeVal, tehsilVal, circleVal]);

  // नगरपंचायत / नगरपरिषद / महानगरपालिका flow: institutions of the selected type
  // across the whole district (each carries its own taluka + मंडळ for auto-fill).
  React.useEffect(() => {
    const valid = orgTypeVal && orgTypeVal !== "-- निवडा --";
    if (valid && !isGP) {
      api.localBodiesByType(orgTypeVal).then((rows) => {
        setTypeBodies(rows || []);
        if (application.data.village && !(rows || []).some((r) => r.lb === application.data.village)) {
          setField("village", "");
          setField("lbWard", "");
        }
      }).catch(() => setTypeBodies([]));
    } else {
      setTypeBodies([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgTypeVal]);

  // Auto-fill तालुका + मंडळ कार्यालय when an institution name is picked (non-GP path)
  const onSelectTypeBody = (lb) => {
    setField("village", lb);
    setField("lbWard", "");
    const rec = typeBodies.find((r) => r.lb === lb);
    if (rec) {
      const mrTaluka = TEHSIL_TALUKA_MR[rec.taluka] || rec.taluka;
      setField("taluka", mrTaluka);
      setField("talukaRO", mrTaluka);
      setField("tehsil", rec.taluka);
      setField("circle", `${rec.taluka}::${rec.circle}`);
    }
  };

  const isUrbanOrg = ["नगरपंचायत", "नगरपरिषद", "महानगरपालिका"].includes(orgTypeVal);
  useEffect(() => {
    if (isUrbanOrg && application.data.village) {
      api.wardsFor(application.data.village).then((ws) => {
        setWards(ws || []);
        if (application.data.lbWard && !(ws || []).includes(application.data.lbWard)) setField("lbWard", "");
      }).catch(() => setWards([]));
    } else {
      setWards([]);
      if (application.data.lbWard) setField("lbWard", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUrbanOrg, application.data.village]);

  useEffect(() => {
    if (!dob) return;
    const d = new Date(dob), now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    setField("age", age >= 0 ? String(age) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dob]);

  useEffect(() => {
    if (taluka && taluka !== "-- निवडा --") {
      setField("talukaRO", taluka);
      if (MR_TALUKA_TEHSIL[taluka]) setField("tehsil", MR_TALUKA_TEHSIL[taluka]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taluka]);

  useEffect(() => {
    if (!application.data.email && user?.email) setField("email", user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Seed default readonly values
  useEffect(() => {
    if (!application.data.districtRO) setField("districtRO", "अमरावती");
    if (!application.data.csc_district) setField("csc_district", "अमरावती");
    if (!application.data.bk_district) setField("bk_district", "अमरावती");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compose full names (मराठी / इंग्रजी) from first/middle/last boxes
  const d = application.data;
  const mrParts = [d.nameMrFirst, d.nameMrMiddle, d.nameMrLast];
  const enParts = [d.nameEnFirst, d.nameEnMiddle, d.nameEnLast];
  useEffect(() => {
    const nameMr = mrParts.filter((p) => p && p.trim()).join(" ").trim();
    if ((application.data.nameMr || "") !== nameMr) setField("nameMr", nameMr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.nameMrFirst, d.nameMrMiddle, d.nameMrLast]);
  useEffect(() => {
    const nameEn = enParts.filter((p) => p && p.trim()).join(" ").trim();
    if ((application.data.nameEn || "") !== nameEn) setField("nameEn", nameEn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.nameEnFirst, d.nameEnMiddle, d.nameEnLast]);

  return (
    <div className="animate-fade-up">
      <Fieldset legend="अर्ज सादर करत असलेली स्थानिक स्वराज्य संस्था येथील आपले सरकार सेवा केंद्र मागणीसाठी अर्ज">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField label="अर्ज करावयाच्या स्थानिक स्वराज्य संस्थेचा प्रकार" name="orgType" required options={ORG_TYPES} errors={errors} />

          {isGP ? (
            <>
              <SelectField label="तालुका" name="taluka" required options={["-- निवडा --", ...Object.keys(MR_TALUKA_TEHSIL)]} errors={errors} />
              <div>
                <label className="mb-1.5 block text-[14px] font-semibold text-slate-700">
                  मंडळ कार्यालय <span className="text-[#E0A106]">*</span>
                </label>
                <select
                  data-testid="step1-circle-select"
                  value={application.data.circle || ""}
                  onChange={(e) => setField("circle", e.target.value)}
                  disabled={!tehsilVal}
                  className={`w-full rounded-lg border bg-white px-3 py-2.5 text-[14px] outline-none focus:border-[#1b5e3f] disabled:bg-slate-50 disabled:text-slate-400 ${errors.circle ? "border-red-400" : "border-slate-300"}`}
                >
                  <option value="">{tehsilVal ? "-- मंडळ निवडा --" : "आधी तालुका निवडा"}</option>
                  {circles.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
                {errors.circle && <p className="mt-1 text-[12.5px] text-red-500">{errors.circle}</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-[14px] font-semibold text-slate-700">
                  स्थानिक स्वराज्य संस्थेचे नाव <span className="text-[#E0A106]">*</span>
                </label>
                <select
                  data-testid="step1-localbody-select"
                  value={application.data.village || ""}
                  onChange={(e) => setField("village", e.target.value)}
                  disabled={!(tehsilVal && circleVal)}
                  className={`w-full rounded-lg border bg-white px-3 py-2.5 text-[14px] outline-none focus:border-[#1b5e3f] disabled:bg-slate-50 disabled:text-slate-400 ${errors.village ? "border-red-400" : "border-slate-300"}`}
                >
                  <option value="" disabled>
                    {!circleVal ? "आधी मंडळ कार्यालय निवडा"
                      : localBodies.length ? "-- संस्था निवडा --"
                      : "या मंडळात ग्रामपंचायत उपलब्ध नाही"}
                  </option>
                  {localBodies.map((lb) => (
                    <option key={lb} value={lb}>{lb}</option>
                  ))}
                </select>
                {errors.village && <p className="mt-1 text-[12.5px] text-red-500">{errors.village}</p>}
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="mb-1.5 block text-[14px] font-semibold text-slate-700">
                  स्थानिक स्वराज्य संस्थेचे नाव <span className="text-[#E0A106]">*</span>
                </label>
                <select
                  data-testid="step1-localbody-select"
                  value={application.data.village || ""}
                  onChange={(e) => onSelectTypeBody(e.target.value)}
                  className={`w-full rounded-lg border bg-white px-3 py-2.5 text-[14px] outline-none focus:border-[#1b5e3f] ${errors.village ? "border-red-400" : "border-slate-300"}`}
                >
                  <option value="" disabled>
                    {typeBodies.length ? "-- संस्था निवडा --" : "या प्रकारची संस्था उपलब्ध नाही"}
                  </option>
                  {typeBodies.map((r) => (
                    <option key={r.lb} value={r.lb}>{r.lb}</option>
                  ))}
                </select>
                {errors.village && <p className="mt-1 text-[12.5px] text-red-500">{errors.village}</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-[14px] font-semibold text-slate-700">तालुका</label>
                <input
                  data-testid="step1-taluka-ro"
                  value={application.data.taluka && application.data.taluka !== "-- निवडा --" ? application.data.taluka : ""}
                  readOnly disabled
                  placeholder="संस्था निवडल्यावर आपोआप भरले जाईल"
                  className="w-full rounded-lg border border-slate-300 bg-slate-100 px-3 py-2.5 text-[14px] text-slate-600 outline-none"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-[14px] font-semibold text-slate-700">मंडळ कार्यालय</label>
                <input
                  data-testid="step1-circle-ro"
                  value={(application.data.circle || "").split("::")[1] || ""}
                  readOnly disabled
                  placeholder="संस्था निवडल्यावर आपोआप भरले जाईल"
                  className="w-full rounded-lg border border-slate-300 bg-slate-100 px-3 py-2.5 text-[14px] text-slate-600 outline-none"
                />
              </div>
            </>
          )}
          {isUrbanOrg && (
            <div>
              <label className="mb-1.5 block text-[14px] font-semibold text-slate-700">
                वॉर्ड / प्रभाग क्रमांक <span className="text-[#E0A106]">*</span>
              </label>
              {wards.length > 0 ? (
                <select
                  data-testid="step1-lbward-select"
                  value={application.data.lbWard || ""}
                  onChange={(e) => setField("lbWard", e.target.value)}
                  disabled={!application.data.village}
                  className={`w-full rounded-lg border bg-white px-3 py-2.5 text-[14px] outline-none focus:border-[#1b5e3f] disabled:bg-slate-50 disabled:text-slate-400 ${errors.lbWard ? "border-red-400" : "border-slate-300"}`}
                >
                  <option value="" disabled>
                    {!application.data.village ? "आधी संस्था निवडा" : "-- वॉर्ड निवडा --"}
                  </option>
                  {wards.map((w) => (<option key={w} value={w}>{w}</option>))}
                </select>
              ) : (
                <input
                  data-testid="step1-lbward-input"
                  value={application.data.lbWard || ""}
                  onChange={(e) => setField("lbWard", e.target.value)}
                  disabled={!application.data.village}
                  placeholder={!application.data.village ? "आधी संस्था निवडा" : "वॉर्ड / प्रभाग क्रमांक लिहा"}
                  className={`w-full rounded-lg border bg-white px-3 py-2.5 text-[14px] outline-none focus:border-[#1b5e3f] disabled:bg-slate-50 disabled:text-slate-400 ${errors.lbWard ? "border-red-400" : "border-slate-300"}`}
                />
              )}
              {errors.lbWard && <p className="mt-1 text-[12.5px] text-red-500">{errors.lbWard}</p>}
            </div>
          )}
        </div>
      </Fieldset>

      <Fieldset legend="अर्जदाराचा फोटो">
        <FileField label="पासपोर्ट आकाराचा फोटो अपलोड करा" name="photo" required note="JPG/PNG, कमाल आकार 1MB" errors={errors} />
      </Fieldset>

      <Fieldset legend="अर्जदाराची वैयक्तिक माहिती">
        {/* Applicant name (मराठी) - first / middle / last */}
        <div className="mb-4">
          <p className="mb-2 text-[14px] font-semibold text-slate-700">अर्जदार यांचे संपूर्ण नाव (मराठी) <span className="text-[#E0A106]">*</span></p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <TextField label="नाव (प्रथम नाव)" name="nameMrFirst" required errors={errors} />
            <TextField label="वडिलांचे / मधले नाव" name="nameMrMiddle" errors={errors} />
            <TextField label="आडनाव" name="nameMrLast" required errors={errors} />
          </div>
        </div>

        {/* Applicant name (English CAPITAL) - first / middle / last */}
        <div className="mb-4">
          <p className="mb-2 text-[14px] font-semibold text-slate-700">अर्जदार यांचे संपूर्ण नाव (इंग्रजी - CAPITAL) <span className="text-[#E0A106]">*</span></p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <TextField label="FIRST NAME" name="nameEnFirst" required uppercase errors={errors} />
            <TextField label="MIDDLE NAME" name="nameEnMiddle" uppercase errors={errors} />
            <TextField label="LAST NAME" name="nameEnLast" required uppercase errors={errors} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField label="जन्म तारीख (DD/MM/YYYY)" name="dob" type="date" required errors={errors} />
          <TextField label="वय (वर्ष)" name="age" type="number" readOnly errors={errors} />
          <SelectField label="लिंग" name="gender" required options={["-- निवडा --", "पुरुष", "स्त्री", "इतर"]} errors={errors} />
          <TextField label="मोबाईल क्रमांक" name="mobile" type="tel" required maxLength={10} errors={errors} />
          <TextField label="ई-मेल आयडी (लॉगिनवरून)" name="email" type="email" required readOnly errors={errors} />
          <div className="hidden sm:block" />
          <TextField label="आधारकार्ड क्रमांक" name="aadhaar" required maxLength={12} errors={errors} />
          <TextField label="पॅनकार्ड क्रमांक" name="pan" required maxLength={10} uppercase errors={errors} />
          <FileField label="आधारकार्ड अपलोड (PDF)" name="aadhaar_file" required note="PDF/JPG/PNG, कमाल आकार 1MB" errors={errors} />
          <FileField label="पॅनकार्ड अपलोड (PDF)" name="pan_file" required note="PDF/JPG/PNG, कमाल आकार 1MB" errors={errors} />
          <div className="md:col-span-2">
            <FileField label="नावात बदल झाला असल्यास दोन्ही नावाची व्यक्ती एकच असल्याबाबतचे गॅझेट / विवाहित स्त्री अर्जदारांच्या बाबतीत विवाह दाखला" name="name_change_file" note="लागू असल्यास अपलोड करा — PDF/JPG/PNG, कमाल आकार 1MB" errors={errors} />
          </div>
        </div>
      </Fieldset>

      <Fieldset legend="अर्जदाराचा रहिवास">
        <div className="mb-4">
          <TextareaField label="रहिवासाचा पत्ता" name="address" rows={2} required errors={errors} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField label="गाव/शहर" name="city" required errors={errors} />
          <TextField label="वॉर्ड क्रमांक (शहरी भागाकरिता)" name="wardNo" errors={errors} />
          <TextField label="तालुका" name="talukaRO" readOnly errors={errors} />
          <TextField label="जिल्हा" name="districtRO" readOnly errors={errors} />
          <TextField label="पिनकोड" name="pincode" required maxLength={6} errors={errors} />
        </div>
        <div className="mt-4">
          <p className="text-[14px] font-semibold text-slate-700">स्थानिक रहिवासी पत्त्याचा पुरावा — कोणतेही दोन <span className="text-[#E0A106]">*</span></p>
          <p className="mb-3 mt-1 text-[12.5px] text-slate-500">शहरी भाग: Voter ID Card, Tax Receipt व Residential Certificate · ग्रामीण भाग: Domicile Certificate, Voter ID Card व Electricity Bill</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FileField label="पुरावा १" name="residence_proof_file" required note="PDF/JPG/PNG, कमाल आकार 1MB" errors={errors} />
            <FileField label="पुरावा २" name="residence_proof_file_2" required note="PDF/JPG/PNG, कमाल आकार 1MB" errors={errors} />
          </div>
        </div>
      </Fieldset>

      <Fieldset legend="स्वघोषणापत्र">
        <div className="rounded-lg border-l-4 border-[#E0A106] bg-[#fdf7e5] p-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[14px] font-semibold text-[#5c4a1c]">स्वघोषणापत्र अपलोड करा <span className="text-[#E0A106]">*</span></p>
            <a
              href="/self-declaration-format.pdf"
              onClick={(e) => openPdf(e, "/self-declaration-format.pdf")}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 rounded-full bg-[#1b5e3f] px-3.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#144d33]"
            >
              <Download className="h-3.5 w-3.5" /> नमुना डाउनलोड करा
            </a>
          </div>
          <p className="mb-3 text-[12.5px] text-[#7a6a45]">वरील नमुन्यानुसार स्वघोषणापत्र तयार करून, स्वाक्षरी करून येथे अपलोड करा.</p>
          <FileField label="स्वघोषणापत्र (PDF/JPG/PNG)" name="self_declaration_file" required note="PDF/JPG/PNG, कमाल आकार 1MB" errors={errors} />
        </div>
      </Fieldset>

      <div className={`rounded-xl border p-4 ${errors.dpdpConsent ? "border-red-400 bg-red-50" : "border-[#e6dcc4] bg-[#fbf9f2]"}`} data-testid="dpdp-consent-box">
        <p className="mb-2 text-[14px] font-bold text-[#1b5e3f]">गोपनीयता सूचना व संमती (DPDP अधिनियम)</p>
        <p className="mb-3 text-[12.5px] leading-relaxed text-[#5c5030]">
          या अर्जाद्वारे गोळा केलेली आपली वैयक्तिक माहिती (नाव, संपर्क, ओळखपत्रे, कागदपत्रे व फोटो) केवळ
          "आपले सरकार सेवा केंद्र" अर्जाची पडताळणी, गुणांकन व निवड प्रक्रियेसाठी जिल्हाधिकारी कार्यालय, अमरावती
          यांच्याकडून वापरली व सुरक्षितपणे साठवली जाईल. आपली माहिती संबंधित शासकीय अधिकाऱ्यांव्यतिरिक्त
          इतर कोणासही दिली जाणार नाही. नाकारलेले / जुने अर्ज व त्यासोबतची कागदपत्रे शासकीय धोरणानुसार
          ठराविक कालावधीनंतर सुरक्षितपणे नष्ट केली जातील. आपली माहिती
          पाहण्याचा / दुरुस्तीचा अधिकार आपणास आहे.
        </p>
        <label className="flex cursor-pointer items-start gap-2.5 text-[13.5px] font-medium text-slate-700">
          <input
            type="checkbox"
            checked={application.data.dpdpConsent === true}
            onChange={(e) => setField("dpdpConsent", e.target.checked)}
            data-testid="dpdp-consent-checkbox"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#1b5e3f]"
          />
          <span>वरील गोपनीयता सूचना मी वाचली असून, माझी वैयक्तिक माहिती वरील उद्देशांसाठी वापरण्यास व साठवण्यास मी संमती देतो/देते. <span className="text-[#E0A106]">*</span></span>
        </label>
        {errors.dpdpConsent && <p className="mt-1.5 text-[12.5px] text-red-500">{errors.dpdpConsent}</p>}
      </div>
    </div>
  );
}

// ===================== STEP 2 : Education =====================
export function Step2({ errors = {} }) {
  const { application } = useApp();
  const d = application.data;
  const rows = ["१२ वी (HSC) उत्तीर्ण *", "पदवी", "पदव्युत्तर पदवी"];
  return (
    <div className="animate-fade-up">
      <Criteria>{CRITERIA[2]}</Criteria>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full border-collapse text-[13.5px]">
          <thead>
            <tr className="bg-[#1b5e3f] text-left text-white">
              <th className="p-2.5 font-semibold">परीक्षेचे नाव</th>
              <th className="p-2.5 font-semibold">उत्तीर्ण आहे का?</th>
              <th className="p-2.5 font-semibold">बोर्ड / विद्यापीठाचे नाव</th>
              <th className="p-2.5 font-semibold">उत्तीर्ण वर्ष</th>
              <th className="p-2.5 font-semibold">टक्केवारी / श्रेणी</th>
              <th className="p-2.5 font-semibold">प्रमाणपत्र / पुरावा अपलोड</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const passed = d[`edu_passed_${i}`] === "yes";
              return (
                <tr key={i} className="border-t border-slate-200">
                  <td className="p-2 align-top font-semibold text-slate-700">{r}</td>
                  <td className="whitespace-nowrap p-2 align-top"><PassRadio name={`edu_passed_${i}`} /></td>
                  <td className="p-2"><Cell name={`edu_board_${i}`} disabled={!passed} invalid={!!errors[`edu_board_${i}`]} /></td>
                  <td className="p-2"><Cell name={`edu_year_${i}`} disabled={!passed} invalid={!!errors[`edu_year_${i}`]} /></td>
                  <td className="p-2"><Cell name={`edu_pct_${i}`} disabled={!passed} invalid={!!errors[`edu_pct_${i}`]} /></td>
                  <td className="min-w-[180px] p-2">
                    {passed ? (
                      <FileField name={`edu_file_${i}`} compact errors={errors} />
                    ) : (
                      <span className="text-[12px] text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[13px] text-slate-500">सूचना: लागू असलेल्या परीक्षेसमोर <strong>होय</strong> निवडा; निवडल्यास त्या ओळीतील सर्व माहिती व प्रमाणपत्र अपलोड आवश्यक आहे. किमान १२ वी (HSC) ची माहिती देणे आवश्यक आहे.</p>
      {errors.edu_passed_1 && <p className="mt-1 text-[12.5px] font-semibold text-[#dc2626]">{errors.edu_passed_1}</p>}
    </div>
  );
}

// ===================== STEP 3 : Computer literacy =====================
export function Step3({ errors = {} }) {
  const { application } = useApp();
  const d = application.data;
  const rows = [
    { label: "MSCIT किंवा समकक्ष प्रमाणपत्र", preset: "MSCIT", star: true },
    { label: "सायबर सुरक्षाबाबतचे प्रमाणपत्र", preset: "" },
    { label: "ऑनलाईन अर्जप्रक्रिया व संगणक हाताळणी बाबतचे किमान 1 वर्ष अनुभवाचे शासकीय शासकीय विभाग प्रमुखाचे  प्रमाणपत्र", preset: "" },
  ];
  return (
    <div className="animate-fade-up">
      <Criteria>{CRITERIA[3]}</Criteria>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full border-collapse text-[13.5px]">
          <thead>
            <tr className="bg-[#1b5e3f] text-left text-white">
              <th className="p-2.5 font-semibold">परीक्षा/प्रमाणपत्राचे नाव</th>
              <th className="p-2.5 font-semibold">उत्तीर्ण आहे का?</th>
              <th className="p-2.5 font-semibold">अभ्यासक्रमाचे नाव</th>
              <th className="p-2.5 font-semibold">उत्तीर्ण वर्ष</th>
              <th className="p-2.5 font-semibold">टक्केवारी/ग्रेड</th>
              <th className="p-2.5 font-semibold">प्रमाणपत्र / पुरावा अपलोड</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const passed = d[`cl_passed_${i}`] === "yes";
              return (
                <tr key={i} className="border-t border-slate-200">
                  <td className="min-w-[220px] p-2 align-top font-semibold text-[#1b5e3f]">
                    {r.label} {r.star && <span className="text-[#E0A106]">*</span>}
                  </td>
                  <td className="whitespace-nowrap p-2 align-top"><PassRadio name={`cl_passed_${i}`} /></td>
                  <td className="p-2"><Cell name={`cl_name_${i}`} value={r.preset || undefined} disabled={!passed} invalid={!!errors[`cl_name_${i}`]} /></td>
                  <td className="p-2"><Cell name={`cl_year_${i}`} disabled={!passed} invalid={!!errors[`cl_year_${i}`]} /></td>
                  <td className="p-2"><Cell name={`cl_pct_${i}`} disabled={!passed} invalid={!!errors[`cl_pct_${i}`]} /></td>
                  <td className="min-w-[180px] p-2">
                    {passed ? (
                      <FileField name={`cl_file_${i}`} compact errors={errors} />
                    ) : (
                      <span className="text-[12px] text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[13px] text-slate-500">सूचना: लागू असलेल्या प्रमाणपत्रासमोर <strong>होय</strong> निवडा; निवडल्यास त्या ओळीतील सर्व माहिती व प्रमाणपत्र अपलोड आवश्यक आहे.</p>
    </div>
  );
}
